import React from 'react';
import { fromJS } from 'immutable';

import FormSelectBoxEntitySelector from 'retired/FormSelectBoxEntitySelector';
import { DeedSubmitter, OpportunityProvider } from 'generated/graphql';
import Organization from 'entities/organization/model';

const getSourceOptions = (organization: Organization): SourceSelectorOption[] => {
  const submitterOptions = [
    {
      type: 'submitter' as const,
      value: DeedSubmitter.Organizers,
      name: 'Admins & nonprofits',
    },
    {
      type: 'submitter' as const,
      value: DeedSubmitter.Employees,
      name: 'Employee created',
    },
  ];

  const opportunityProviders = Object.values(OpportunityProvider);

  const availableProviders = opportunityProviders.filter(
    (source) => !organization?.getIn(['settings', 'disabledOpportunityProviders'])?.includes(source)
  );

  const providerOptions = availableProviders.map((provider) => ({
    type: 'provider' as const,
    value: provider,
    name: OpportunityProvider[provider],
  }));

  // @TODO-VZ: Check if it ever worked. If so then do we need deedPartnerPrefix?
  const subsidiaryOptions = (organization?.siblingSubsidiaries || []).map((subsidiary) => ({
    type: 'subsidiary' as const,
    value: String(subsidiary._id),
    name: subsidiary.name || '',
  }));

  return [...submitterOptions, ...providerOptions, ...subsidiaryOptions];
};

export type SourceSelectorOption =
  | {
      type: 'provider';
      value: OpportunityProvider;
      name: string;
    }
  | {
      type: 'submitter';
      value: DeedSubmitter;
      name: string;
    }
  | {
      type: 'subsidiary';
      value: string;
      name: string;
    };

interface Props {
  organization: Organization;
  sources: SourceSelectorOption[];
  onChange: (values: SourceSelectorOption[]) => void;
}

const SourceSelector = ({ organization, sources, onChange }: Props) => {
  const options = getSourceOptions(organization);

  const mappedOptions = fromJS(
    options.map((option) => ({
      id: `${option.type}__${option.value}`,
      name: option.name,
    }))
  );

  const mappedOnChange = (values: string[]) => {
    onChange(
      values.map((mappedValue) => {
        const [type, value] = mappedValue.split('__');
        const selectedOption = options.find(
          (option) => option.value === value && option.type === type
        );
        if (!selectedOption) {
          throw new Error('Make TS happy');
        }
        return selectedOption;
      })
    );
  };

  const mappedValue = sources.map((source) => `${source.type}__${source.value}`);

  return (
    <FormSelectBoxEntitySelector
      multiple
      options={mappedOptions}
      placeholder="Source"
      allowClear
      input={{
        onChange: mappedOnChange,
        value: mappedValue,
      }}
    />
  );
};

export default SourceSelector;
